import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, ArrowRight, CheckCircle2, MessageCircle } from "lucide-react";
import { useConsultation } from "@/context/ConsultationContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { api, track } from "@/lib/api";
import { WA_LINK } from "@/data/content";

const BUILD_OPTIONS = ["Independent Home", "Villa", "Duplex", "Apartment", "Commercial", "Renovation", "Other"];
const LOCATION_OPTIONS = ["Tumakuru", "Bengaluru", "Other"];
const FLOOR_OPTIONS = ["Ground", "G+1", "G+2", "G+3", "G+4", "G+5", "G+6", "Custom"];
const TIMELINE_OPTIONS = ["Immediately", "1–3 months", "3–6 months", "6–12 months", "Just planning", "Custom"];
const PREF_OPTIONS = ["Basic", "Standard", "Premium", "Custom", "Not sure — need guidance"];

const inputCls =
  "w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-offwhite placeholder:text-white/35 focus:border-gold focus:outline-none";

const OptionGrid = ({ options, value, onSelect, testidPrefix }) => (
  <div className="grid grid-cols-2 gap-3">
    {options.map((opt) => (
      <button
        key={opt}
        type="button"
        data-testid={`${testidPrefix}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        aria-pressed={value === opt}
        onClick={() => onSelect(opt)}
        className={`rounded-xl border px-4 py-3.5 text-left text-sm font-semibold transition-[border-color,background-color] duration-200 ${
          value === opt
            ? "border-gold bg-gold/15 text-gold"
            : "border-white/15 bg-white/5 text-offwhite hover:border-white/40"
        }`}
      >
        {opt}
      </button>
    ))}
  </div>
);

export const ConsultationModal = () => {
  const { open, setOpen } = useConsultation();
  const { t } = useLanguage();
  const [step, setStep] = useState(0);
  const [data, setData] = useState({});
  const [status, setStatus] = useState("idle");
  const [errors, setErrors] = useState({});

  const reset = () => { setStep(0); setData({}); setStatus("idle"); setErrors({}); };
  const handleOpenChange = (v) => { setOpen(v); if (!v) setTimeout(reset, 300); };
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const next = () => { track("consultation_step", { step: step + 1 }); setStep((s) => Math.min(s + 1, 4)); };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    const errs = {};
    if (!data.name || data.name.trim().length < 2) errs.name = t("form.nameRequired");
    let digits = (data.phone || "").replace(/\D/g, "");
    if (digits.length === 12 && digits.startsWith("91")) digits = digits.slice(2);
    if (!digits) errs.phone = t("form.phoneRequired");
    else if (digits.length !== 10 || !/^[6-9]/.test(digits)) errs.phone = t("form.phoneInvalid");
    if (data.email && !/^\S+@\S+\.\S+$/.test(data.email)) errs.email = t("form.emailInvalid");
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setStatus("submitting");
    track("consultation_form_submit");
    try {
      await api.post("/enquiries", {
        name: data.name.trim(),
        phone: data.phone,
        email: data.email || null,
        form_type: "consultation",
        project_type: data.project_type || null,
        location: data.location || null,
        plot_area: data.plot_area || null,
        built_up_area: data.built_up_area || null,
        floors: data.floors || null,
        timeline: data.timeline || null,
        preference: data.preference || null,
        message: data.message || null,
        company: data.company || "",
      });
      setStatus("success");
      track("consultation_form_success");
    } catch {
      setStatus("error");
    }
  };

  const stepTitles = [t("consult.step1"), t("consult.step2"), t("consult.step3"), t("consult.step4"), t("consult.step5")];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="consultation-modal" className="border-white/10 bg-charcoal text-offwhite sm:max-w-lg">
        <DialogTitle className="sr-only">{t("cta.consult")}</DialogTitle>

        {status === "success" ? (
          <div className="py-6 text-center" data-testid="consult-success">
            <CheckCircle2 className="mx-auto h-14 w-14 text-gold" />
            <h3 className="mt-5 font-display text-3xl">{t("consult.successTitle")}</h3>
            <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-white/70">{t("consult.success")}</p>
            <a
              href={WA_LINK()}
              target="_blank"
              rel="noreferrer"
              data-testid="consult-success-whatsapp-button"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-bold text-charcoal transition-transform hover:-translate-y-0.5"
            >
              <MessageCircle className="h-4 w-4" /> {t("estimate.continueWa")}
            </a>
          </div>
        ) : (
          <>
            <div className="mb-2 flex items-center justify-between">
              <p className="eyebrow-dark">{t("consult.stepOf").replace("{x}", step + 1)}</p>
              <div className="flex gap-1.5">
                {[0, 1, 2, 3, 4].map((i) => (
                  <span key={i} className={`h-1 w-6 rounded-full transition-colors ${i <= step ? "bg-gold" : "bg-white/15"}`} />
                ))}
              </div>
            </div>
            <h3 className="font-display text-2xl md:text-3xl">{stepTitles[step]}</h3>

            <div className="mt-6 min-h-[220px]">
              {step === 0 && (
                <OptionGrid options={BUILD_OPTIONS} value={data.project_type} onSelect={(v) => set("project_type", v)} testidPrefix="consult-build" />
              )}
              {step === 1 && (
                <OptionGrid options={LOCATION_OPTIONS} value={data.location} onSelect={(v) => set("location", v)} testidPrefix="consult-location" />
              )}
              {step === 2 && (
                <div className="space-y-4">
                  <input data-testid="consult-plot-input" className={inputCls} placeholder="Plot area, e.g. 30×40 (optional)"
                    value={data.plot_area || ""} onChange={(e) => set("plot_area", e.target.value)} />
                  <input data-testid="consult-builtup-input" className={inputCls} placeholder="Built-up area in sq ft (optional)"
                    value={data.built_up_area || ""} onChange={(e) => set("built_up_area", e.target.value)} />
                  <select data-testid="consult-floors-select" className={inputCls} value={data.floors || ""} onChange={(e) => set("floors", e.target.value)}>
                    <option value="" className="bg-charcoal">Floors (optional)</option>
                    {FLOOR_OPTIONS.map((f) => <option key={f} value={f} className="bg-charcoal">{f}</option>)}
                  </select>
                </div>
              )}
              {step === 3 && (
                <div className="space-y-5">
                  <OptionGrid options={TIMELINE_OPTIONS} value={data.timeline} onSelect={(v) => set("timeline", v)} testidPrefix="consult-timeline" />
                  <select data-testid="consult-preference-select" className={inputCls} value={data.preference || ""} onChange={(e) => set("preference", e.target.value)}>
                    <option value="" className="bg-charcoal">Construction preference (optional)</option>
                    {PREF_OPTIONS.map((p) => <option key={p} value={p} className="bg-charcoal">{p}</option>)}
                  </select>
                </div>
              )}
              {step === 4 && (
                <div className="space-y-4">
                  <div>
                    <input data-testid="consult-name-input" className={inputCls} placeholder={`${t("form.name")} *`}
                      value={data.name || ""} onChange={(e) => set("name", e.target.value)} aria-invalid={!!errors.name} />
                    {errors.name && <p className="mt-1.5 text-xs text-red-400" data-testid="consult-name-error">{errors.name}</p>}
                  </div>
                  <div>
                    <input data-testid="consult-phone-input" className={inputCls} placeholder={`${t("form.phone")} *`} inputMode="tel"
                      value={data.phone || ""} onChange={(e) => set("phone", e.target.value)} aria-invalid={!!errors.phone} />
                    {errors.phone && <p className="mt-1.5 text-xs text-red-400" data-testid="consult-phone-error">{errors.phone}</p>}
                  </div>
                  <div>
                    <input data-testid="consult-email-input" className={inputCls} placeholder={t("form.email")} inputMode="email"
                      value={data.email || ""} onChange={(e) => set("email", e.target.value)} aria-invalid={!!errors.email} />
                    {errors.email && <p className="mt-1.5 text-xs text-red-400" data-testid="consult-email-error">{errors.email}</p>}
                  </div>
                  <textarea data-testid="consult-message-input" className={`${inputCls} min-h-[72px] resize-none`} placeholder={t("form.message")}
                    value={data.message || ""} onChange={(e) => set("message", e.target.value)} />
                  <input type="text" name="company" tabIndex={-1} autoComplete="off" aria-hidden="true"
                    className="pointer-events-none absolute h-0 w-0 opacity-0"
                    value={data.company || ""} onChange={(e) => set("company", e.target.value)} />
                  {status === "error" && (
                    <p className="rounded-xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-xs text-red-300" data-testid="consult-error">
                      {t("form.error")}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="mt-6 flex items-center justify-between">
              {step > 0 ? (
                <button type="button" data-testid="consult-back-button" onClick={back}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/60 transition-colors hover:text-white">
                  <ArrowLeft className="h-4 w-4" /> {t("cta.back")}
                </button>
              ) : <span />}
              <div className="flex items-center gap-4">
                {step < 4 && (
                  <button type="button" data-testid="consult-skip-button" onClick={next}
                    className="text-sm font-semibold text-white/50 underline-offset-4 transition-colors hover:text-gold hover:underline">
                    {t("cta.skip")}
                  </button>
                )}
                {step < 4 ? (
                  <button type="button" data-testid="consult-next-button" onClick={next}
                    className="group inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-bold text-charcoal transition-transform hover:-translate-y-0.5">
                    {t("cta.next")} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </button>
                ) : (
                  <button type="button" data-testid="consult-submit-button" onClick={submit} disabled={status === "submitting"}
                    className="rounded-full bg-gold px-6 py-3 text-sm font-bold text-charcoal transition-transform hover:-translate-y-0.5 disabled:opacity-60">
                    {status === "submitting" ? t("form.sending") : t("cta.submit")}
                  </button>
                )}
              </div>
            </div>
            {step < 4 && (
              <button type="button" data-testid="consult-skipall-button" onClick={() => setStep(4)}
                className="mt-4 w-full text-center text-xs text-white/40 underline-offset-4 transition-colors hover:text-gold hover:underline">
                {t("cta.skipAll")}
              </button>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
